import type { ColumnDef } from "@tanstack/react-table";
import { useEffect } from "react";
import { formatIntToCurrency } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import type { Receipt } from "~/managers/use-register-manager";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { api } from "~/lib/api";
import { useOrderStore } from "~/stores/use-order-store";
import { useReportStore } from "~/stores/use-report-store";

export const Route = createFileRoute("/_main/reports/_layout/receipt")({
  component: ReceiptReports,
});

function ReceiptReports() {
  const { range } = useReportStore();

  const { t } = useTranslation("common");

  const { setReceiptDialogOpen, setSelectedReceipt } = useOrderStore();

  const { data: reportData, refetch } = api.report.getReceipts.useQuery(
    {
      startDate: range.from,
      endDate: range.to,
    },
    {
      initialData: [],
      staleTime: 0,
    },
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const columns: ColumnDef<Receipt>[] = [
    {
      id: t("Report.date"),
      accessorKey: "createdAt",
      filterFn: "includesString",
      sortingFn: (rowA, rowB) => {
        return (
          rowA.original.createdAt.getTime() - rowB.original.createdAt.getTime()
        );
      },
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report.date")} />
      ),
      cell: ({ row: { original: receipt } }) => {
        return <p>{format(receipt.createdAt, "Pp")}</p>;
      },
    },
    {
      id: t("Report.receipt_number"),
      accessorKey: "number",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.receipt_number")}
        />
      ),
      cell: ({ row: { original: receipt } }) => {
        return <p>{receipt.number}</p>;
      },
    },
    {
      id: t("Report.total_price"),
      accessorKey: "orderTotal",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report.total_price")}
        />
      ),
      cell: ({ row: { original: receipt } }) => {
        return <p>{formatIntToCurrency(receipt.orderTotal)}</p>;
      },
    },
    {
      id: t("Report_total_paid"),
      accessorKey: "totalPaid",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report_total_paid")} />
      ),
      cell: ({ row: { original: receipt } }) => {
        return <p>{formatIntToCurrency(receipt.totalPaid)}</p>;
      },
    },
    {
      id: t("Report_payment_method"),
      accessorKey: "paymentMethods",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("Report_payment_method")}
        />
      ),
      cell: ({ row: { original: receipt } }) => {
        return <p>{receipt.paymentMethods.join(", ")}</p>;
      },
    },
    {
      id: t("Report_table"),
      accessorKey: "table",
      filterFn: "includesString",
      size: 10,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("Report_table")} />
      ),
      cell: ({ row: { original: receipt } }) => {
        return <p>{receipt.table}</p>;
      },
    },
  ];

  // const combinedPayments = groupBy(reportData.payments, "methodName");

  // const combinedRevenue = groupBy(reportData.receipts, "hourMinute");

  // const paymentsChartData = Object.entries(combinedPayments).map(
  //   ([name, payments], index) => ({
  //     paymentMethod: name,
  //     amount: payments.reduce((acc, payment) => acc + payment.amount, 0),
  //     fill: `hsl(var(--chart-${index + 1}))`,
  //   }),
  // );

  // const paymentsAmountChartData = Object.entries(combinedPayments).map(
  //   ([name, payments], index) => ({
  //     paymentMethod: name,
  //     payments: payments.length,
  //     fill: `hsl(var(--chart-${index + 1}))`,
  //   }),
  // );

  // const revenueChartConfig = {
  //   revenue: {
  //     label: "Revenue",
  //     color: "hsl(var(--chart-1))",
  //   },
  // } satisfies ChartConfig;

  // const revenueChartData = Object.entries(combinedRevenue).map(
  //   ([name, receipt], index) => ({
  //     hourMinute: name,
  //     revenue: receipt.reduce((acc, receipt) => acc + receipt.total, 0),
  //     fill: `hsl(var(--chart-${index + 1}))`,
  //   }),
  // );

  // const paymentsChartConfig = Object.fromEntries(
  //   Object.entries(combinedPayments).map(([key], index) => [
  //     key,
  //     {
  //       label: key,
  //       color: `hsl(var(--chart-${index + 1}))`,
  //     },
  //   ]),
  // ) satisfies ChartConfig;

  // const paymentsAmountChartConfig = Object.fromEntries(
  //   Object.entries(combinedPayments).map(([key], index) => [
  //     key,
  //     {
  //       label: key,
  //       color: `hsl(var(--chart-${index + 1}))`,
  //     },
  //   ]),
  // ) satisfies ChartConfig;

  return (
    <div className="flex flex-col gap-4">
      {/* <Collapsible defaultOpen={true}>
        <CollapsibleTrigger>Charts</CollapsibleTrigger>
        <CollapsibleContent>
          <div className="flex flex-col gap-4 md:flex-row">
            <Card className="flex flex-1 flex-col">
              <CardContent className="h-full">
                {revenueChartData.length > 0 && (
                  <ChartContainer config={revenueChartConfig}>
                    <LineChart
                      accessibilityLayer
                      data={revenueChartData}
                      margin={{
                        left: 12,
                        right: 12,
                      }}
                    >
                      <CartesianGrid vertical={false} />
                      <YAxis
                        dataKey="revenue"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                      />
                      <XAxis
                        dataKey="hourMinute"
                        tickLine={false}
                        axisLine={false}
                        tickMargin={8}
                        tickFormatter={(unixTime) =>
                          format(fromUnixTime(unixTime as number), "HH:mm")
                        }
                      />
                      <ChartTooltip
                        cursor={false}
                        content={<ChartTooltipContent hideLabel />}
                      />
                      <Line
                        dataKey="revenue"
                        type="linear"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ChartContainer>
                )}
                {revenueChartData.length === 0 && <NoData />}
              </CardContent>
            </Card>
            <Card className="flex flex-1 flex-col">
              <CardContent className="h-full flex-1">
                {paymentsAmountChartData.length > 0 && (
                  <ChartContainer
                    config={paymentsAmountChartConfig}
                    className="mx-auto aspect-square max-h-[250px] min-h-[50px] pb-0 [&_.recharts-pie-label-text]:fill-foreground"
                  >
                    <PieChart>
                      <ChartTooltip
                        content={<ChartTooltipContent hideLabel />}
                      />
                      <Pie
                        data={paymentsAmountChartData}
                        dataKey="payments"
                        label
                        nameKey="paymentMethod"
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                    </PieChart>
                  </ChartContainer>
                )}
                {paymentsAmountChartData.length === 0 && <NoData />}
              </CardContent>
            </Card>
            <Card className="flex flex-1 flex-col">
              <CardContent className="flex-1">
                {paymentsChartData.length > 0 && (
                  <ChartContainer
                    config={paymentsChartConfig}
                    className="mx-auto aspect-square max-h-[250px] min-h-[50px] pb-0 [&_.recharts-pie-label-text]:fill-foreground"
                  >
                    <PieChart>
                      <ChartTooltip
                        content={<ChartTooltipContent hideLabel />}
                      />
                      <Pie
                        data={paymentsChartData}
                        dataKey="amount"
                        label
                        nameKey="paymentMethod"
                      />
                      <ChartLegend content={<ChartLegendContent />} />
                    </PieChart>
                  </ChartContainer>
                )}
                {paymentsChartData.length === 0 && <NoData />}
              </CardContent>
            </Card>
          </div>
        </CollapsibleContent>
      </Collapsible> */}

      <DataTable
        defaultSorting={[{ id: t("Report.receipt_number"), desc: true }]}
        pdfExport={{
          filename: "receipt-report",
        }}
        csvExport={{
          filename: "receipt-report",
        }}
        onSelect={(row) => {
          setSelectedReceipt({
            receipt: row,
            showCredit: true,
          });
          setReceiptDialogOpen(true);
        }}
        columns={columns}
        data={reportData}
      />
    </div>
  );
}
